
import React, { useState, useEffect } from "react";
import { Loader2, Clock, MapPin, MessageSquare } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { TimeEntry } from "./types";
import { parseCoordinates, parseBreaks, formatDuration, formatLocation } from "./utils";

interface TrackingTabProps {
  timeEntries: TimeEntry[];
  loading: boolean;
}

interface LocationDisplayProps {
  coords: any;
  label: string;
}

const LocationDisplay: React.FC<LocationDisplayProps> = ({ coords, label }) => {
  const [address, setAddress] = useState<string>("Chargement...");

  useEffect(() => {
    const loadAddress = async () => {
      try {
        const formattedLocation = await formatLocation(coords);
        setAddress(formattedLocation);
      } catch (error) {
        console.error("Erreur lors du chargement de l'adresse:", error);
        const parsedCoords = parseCoordinates(coords);
        if (parsedCoords) {
          setAddress(`${parsedCoords.latitude.toFixed(6)}, ${parsedCoords.longitude.toFixed(6)}`);
        } else {
          setAddress("Non disponible");
        }
      }
    };

    loadAddress();
  }, [coords]);

  return <p>{label}: {address}</p>;
};

export const TrackingTab: React.FC<TrackingTabProps> = ({ 
  timeEntries, 
  loading 
}) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-4">
        <h3 className="text-lg font-bold text-[#333333] mb-4">Historique des pointages</h3>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 text-[#BD1E28] animate-spin" />
          <span className="ml-2">Chargement des données...</span>
        </div>
      </div>
    );
  }

  if (timeEntries.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-4">
        <h3 className="text-lg font-bold text-[#333333] mb-4">Historique des pointages</h3>
        <p className="text-center text-[#666666] py-8">Aucun pointage trouvé</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-4">
      <h3 className="text-lg font-bold text-[#333333] mb-4">Historique des pointages</h3>
      
      <div className="space-y-4">
        {timeEntries.map((entry) => {
          const breaks = parseBreaks(entry.breaks);
          
          return (
            <div key={entry.id} className="border rounded-lg p-4 bg-gray-50">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h4 className="font-bold text-[#333333]">{entry.worksites?.name || "Chantier inconnu"}</h4>
                  <p className="text-sm text-[#666666]">
                    {format(new Date(entry.date), "EEEE d MMMM yyyy", { locale: fr })}
                  </p>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  entry.status === 'completed' ? 'bg-green-100 text-green-800' :
                  entry.status === 'active' ? 'bg-blue-100 text-blue-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {entry.status === 'completed' ? 'Terminé' :
                   entry.status === 'active' ? 'En cours' : 
                   'En attente'}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex items-center text-sm">
                    <Clock className="w-4 h-4 text-[#666666] mr-2" />
                    <span className="font-medium">Horaires:</span>
                  </div>
                  <div className="ml-6 text-sm text-[#666666]">
                    <p>Début: {entry.start_time}</p>
                    <p>Fin: {entry.end_time || "En cours"}</p>
                    <p>Durée: {formatDuration(entry.start_time, entry.end_time)}</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center text-sm">
                    <MapPin className="w-4 h-4 text-[#666666] mr-2" />
                    <span className="font-medium">Localisation:</span>
                  </div>
                  <div className="ml-6 text-sm text-[#666666]">
                    <LocationDisplay coords={entry.start_coordinates} label="Arrivée" />
                    {entry.end_coordinates && (
                      <LocationDisplay coords={entry.end_coordinates} label="Départ" />
                    )}
                  </div>
                </div>
              </div>

              {breaks && breaks.length > 0 && (
                <div className="mt-4">
                  <div className="flex items-center text-sm mb-2">
                    <Clock className="w-4 h-4 text-[#666666] mr-2" />
                    <span className="font-medium">Pauses:</span>
                  </div>
                  <div className="ml-6 space-y-1">
                    {breaks.map((breakEntry, index) => (
                      <p key={index} className="text-sm text-[#666666]">
                        {breakEntry.startTime} - {breakEntry.endTime || "En cours"}
                        {breakEntry.duration && ` (${Math.floor(breakEntry.duration / 60)}min)`}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {entry.comment && (
                <div className="mt-4">
                  <div className="flex items-center text-sm mb-2">
                    <MessageSquare className="w-4 h-4 text-[#666666] mr-2" />
                    <span className="font-medium">Commentaire:</span>
                  </div>
                  <p className="ml-6 text-sm text-[#666666] bg-white p-2 rounded border">
                    {entry.comment}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
